import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Report, ReportFilters, Problem, Severity, GroupedPrescriptions, RawReportPayload } from '../types/report';
import { TranslationStats } from '../types/historical';
import { reportService } from '../services/reportService';
import { normalizeReport, groupPrescriptions } from '../utils/reportTransform';
import { USE_BACKEND } from '../config/env';
import { authenticatedHistoricalReportService, backendHistoricalReportService } from '../services/historicalReportService';

export interface UseReportOptions {
    /** Data object UUID for backend API calls, or dataset ID for mock data */
    dataObjectUuid?: string;
    /** Historical report ID for fetching existing reports */
    reportId?: string;
    /** Legacy dataset ID - maintained for backward compatibility with mocks */
    datasetId?: string;
    /** Display name for the data object */
    dataObjectName?: string;
    /** Override email for fetching report (admin viewing another user's report) */
    ownerEmail?: string;
    /** Whether the hook should automatically fetch data */
    enabled?: boolean;
    /** Whether to refresh data when window gains focus */
    refreshOnFocus?: boolean;
    /** Target language for inline translation (e.g. 'hi', 'fr'). Omit or 'en' for original. */
    targetLanguage?: string;
}

export interface UseReportState {
    report: Report | null;
    loading: boolean;
    error: Error | null;
    refreshing: boolean;
    filters: ReportFilters;
    setFilters: (next: Partial<ReportFilters>) => void;
    filteredProblems: Problem[];
    groupedPrescriptions: GroupedPrescriptions;
    refresh: () => Promise<void>;
    lastUpdatedAt: number | null;
    availableSeverities: Severity[];
    availableCategories: string[];
    /** Translation stats from last inline-translated fetch (null when language is 'en') */
    translationStats: TranslationStats | null;
}

/**
 * Hook for managing report data, filtering, and state
 */
export function useReport({
    dataObjectUuid,
    reportId,
    datasetId,
    dataObjectName,
    ownerEmail,
    enabled = true,
    targetLanguage,
}: UseReportOptions): UseReportState {
    const [report, setReport] = useState<Report | null>(null);
    const [loading, setLoading] = useState(enabled);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const [lastUpdatedAt, setLastUpdatedAt] = useState<number | null>(null);
    const [translationStats, setTranslationStats] = useState<TranslationStats | null>(null);

    // In-memory cache: reportId:lang → Report (avoids re-fetching on language toggle)
    const reportCacheRef = useRef<Map<string, { report: Report; stats: TranslationStats | null }>>(new Map());

    // Filter state
    const [filters, setFiltersState] = useState<ReportFilters>({
        severities: new Set(),
        categories: new Set(),
        query: '',
    });

    const fetchReport = useCallback(async (isRefresh = false) => {
        if (!enabled) {
            return;
        }

        if (isRefresh) {
            setRefreshing(true);
        } else {
            setLoading(true);
        }

        try {
            let report: Report;

            // Priority 1: Historical report ID (from clicking on historical reports table)
            if (USE_BACKEND && reportId) {
                // Check in-memory cache for this reportId + language combo
                const langKey = targetLanguage || 'en';
                const cacheKey = `${reportId}:${langKey}`;
                const cached = reportCacheRef.current.get(cacheKey);
                if (cached && !isRefresh) {
                    setReport(cached.report);
                    setTranslationStats(cached.stats);
                    setError(null);
                    setLastUpdatedAt(Date.now());
                    return;
                }

                try {
                    // If ownerEmail is provided (admin viewing another user's report),
                    // call backend directly with that email instead of the logged-in user's email
                    const historicalReport = ownerEmail
                        ? await backendHistoricalReportService.getReportDetail(reportId, ownerEmail, targetLanguage)
                        : await authenticatedHistoricalReportService.getReportDetail(reportId, targetLanguage);

                    // The historical report should contain the full report JSON
                    if (historicalReport.raw_report) {
                        // The backend response has a different structure than RawReportPayload
                        // Transform it to match the expected format
                        const backendReport = historicalReport.raw_report;


                        // Transform detailed problems to match expected format
                        const transformedProblems = ((backendReport as any).detailed_problems || []).map((problem: any) => ({
                            Issue: problem.issue || '',
                            Problem_Details: problem.problem_details || problem.description || '',
                            Solution_Strategy: problem.solution_strategy || '',
                            Business_Impact: problem.business_impact || '',
                            Technical_Analysis: problem.technical_analysis || {},
                            Action_Plan: problem.action_plan || [],
                            column_name: problem.column_name || problem.Column_Name
                        }));

                        // Transform health score to match expected format
                        const transformedHealthScore = { ...(backendReport as any).health_score };
                        if (transformedHealthScore.data_quality_assessment_6_dimensions) {
                            transformedHealthScore['Data Quality Assessment (6 Dimensions)'] = transformedHealthScore.data_quality_assessment_6_dimensions;
                            delete transformedHealthScore.data_quality_assessment_6_dimensions;
                        }

                        // Transform backend format to RawReportPayload format
                        // Ensure executive_summary is always an array for normalizeReport compatibility
                        let executiveSummary = (backendReport as any).executive_summary || [];
                        if (!Array.isArray(executiveSummary)) {
                            // If it's a string or object, wrap it in an array
                            executiveSummary = [executiveSummary];
                        }



                        // Extract data_object_description dynamically from multiple possible locations
                        // Newer reports: raw_report.data_object_description
                        // Older reports: raw_report.health_score.data_object_description
                        const dataObjectDescription =
                            (backendReport as any).data_object_description ||
                            (backendReport as any).health_score?.data_object_description ||
                            historicalReport.data_object_description ||
                            null;
                        const hasValidDataObjectDescription = dataObjectDescription &&
                            typeof dataObjectDescription === 'object' &&
                            (dataObjectDescription.total_rows !== undefined ||
                                dataObjectDescription.total_columns !== undefined ||
                                dataObjectDescription.description ||
                                dataObjectDescription.dataset_name ||
                                dataObjectDescription.table_name);

                        // Enhanced data object name extraction from multiple backend sources
                        const historicalDataObjectName = (backendReport as any).data_object_name ||
                            historicalReport.data_object_name ||
                            (backendReport as any).original_filename ||
                            dataObjectDescription?.dataset_name ||
                            dataObjectDescription?.table_name ||
                            dataObjectDescription?.name ||
                            dataObjectDescription?.table_full_name ||
                            dataObjectDescription?.original_filename;
                        const effectiveDataObjectName = dataObjectName || historicalDataObjectName;

                        const reportData = {
                            'Health Score': transformedHealthScore,
                            'Executive Summary': executiveSummary,
                            'Detailed Problems': transformedProblems,
                            'Analysis Metadata': (backendReport as any).analysis_metadata || {},
                            'reasoning_result': (backendReport as any).reasoning_result,
                            'dq_framework_results': (backendReport as any).dq_framework_results,
                            ...(hasValidDataObjectDescription && { 'Data Object Description': dataObjectDescription })
                        };

                        report = normalizeReport(reportData as RawReportPayload, effectiveDataObjectName);

                        // Capture inline translation stats if present
                        const stats = historicalReport.translation_stats ?? null;
                        setTranslationStats(stats);

                        // Cache the result
                        reportCacheRef.current.set(cacheKey, { report, stats });
                    } else {
                        throw new Error('Historical report does not contain raw report data');
                    }
                } catch (error) {
                    throw new Error(`Failed to load historical report: ${error instanceof Error ? error.message : 'Unknown error'}`);
                }
            }
            // Priority 2: Data object UUID (for live analysis results)
            else if (USE_BACKEND && dataObjectUuid) {
                // Try to get cached analysis results directly (most common case)
                let analysisResponse = await reportService.getAnalysisResults(dataObjectUuid);

                if (analysisResponse) {
                    // Always process raw API response format (no caching complexity)
                    const backendReport = analysisResponse;

                    // Transform executive_summary object to array format for normalizeReport
                    const executiveSummaryObj = (backendReport as any).executive_summary || {};
                    const executiveSummary = [
                        executiveSummaryObj.dataset_overview || '',
                        executiveSummaryObj.most_important_issues || '',
                        executiveSummaryObj.next_steps || ''
                    ].filter(item => item.trim() !== '');

                    // Transform health_score to expected format
                    const transformedHealthScore = { ...(backendReport as any).health_score };
                    if (transformedHealthScore.data_quality_assessment_6_dimensions) {
                        transformedHealthScore['Data Quality Assessment (6 Dimensions)'] = transformedHealthScore.data_quality_assessment_6_dimensions;
                        delete transformedHealthScore.data_quality_assessment_6_dimensions;
                    }

                    // No detailed_problems in raw format - will be generated from reasoning_result
                    const transformedProblems: any[] = [];

                    // Extract data_object_description dynamically from multiple possible locations
                    const dataObjectDescription =
                        (backendReport as any).data_object_description ||
                        (backendReport as any).health_score?.data_object_description ||
                        null;
                    const hasValidDataObjectDescription = dataObjectDescription &&
                        typeof dataObjectDescription === 'object' &&
                        (dataObjectDescription.total_rows !== undefined ||
                            dataObjectDescription.total_columns !== undefined ||
                            dataObjectDescription.description ||
                            dataObjectDescription.dataset_name ||
                            dataObjectDescription.table_name);

                    const reportData = {
                        'Health Score': transformedHealthScore,
                        'Executive Summary': executiveSummary,
                        'Detailed Problems': transformedProblems,
                        'Analysis Metadata': {},
                        'reasoning_result': (backendReport as any).reasoning_result,
                        'executive_summary': (backendReport as any).executive_summary,
                        'dq_framework_results': (backendReport as any).dq_framework_results,
                        ...(hasValidDataObjectDescription && { 'Data Object Description': dataObjectDescription })
                    };

                    report = normalizeReport(reportData as RawReportPayload, dataObjectName);

                } else {
                    // No cached results found - this means analysis hasn't been run yet
                    throw new Error('No analysis results found for this data object. Please upload the file again to generate a report.');
                }
            }
            // Priority 3: Mock data fallback
            else if (datasetId) {
                // Fallback to mock data system
                const rawReport = await reportService.getReport(datasetId);
                report = normalizeReport(rawReport, dataObjectName);
            } else {
                throw new Error('Either reportId, dataObjectUuid (for backend) or datasetId (for mocks) must be provided');
            }

            setReport(report);
            setError(null);
            setLastUpdatedAt(Date.now());
        } catch (e) {
            const error = e instanceof Error ? e : new Error('Failed to fetch report');
            console.error('Report fetch error:', error);
            setError(error);

            // If this is initial load, clear any existing report
            if (!isRefresh) {
                setReport(null);
            }
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [dataObjectUuid, reportId, datasetId, dataObjectName, ownerEmail, enabled, targetLanguage]);

    // Initial fetch
    useEffect(() => {
        fetchReport(false);
    }, [fetchReport]);

    // Filter management
    const setFilters = useCallback((next: Partial<ReportFilters>) => {
        setFiltersState(prev => ({
            ...prev,
            ...next,
            // Handle Set merging for severities and categories
            severities: next.severities ?? prev.severities,
            categories: next.categories ?? prev.categories,
        }));
    }, []);

    // Derived data - memoized for performance
    const filteredProblems = useMemo(() => {
        if (!report?.problems) return [];

        let problems = report.problems;

        // Filter by severities
        if (filters.severities.size > 0) {
            problems = problems.filter(p => filters.severities.has(p.severity));
        }

        // Filter by categories
        if (filters.categories.size > 0) {
            problems = problems.filter(p => p.category && filters.categories.has(p.category));
        }

        // Filter by search query
        if (filters.query.trim()) {
            const query = filters.query.toLowerCase();
            problems = problems.filter(p => {
                const issue = p.issue?.toLowerCase() || '';
                const businessImpact = p.businessImpact?.toLowerCase() || '';
                const problemDetails = p.problemDetails?.toLowerCase() || '';

                return issue.includes(query) ||
                    businessImpact.includes(query) ||
                    problemDetails.includes(query);
            });
        }

        return problems;
    }, [report?.problems, filters]);

    const groupedPrescriptions = useMemo(() => {
        if (!report?.prescriptions) {
            return { high: [], medium: [], low: [] };
        }
        return groupPrescriptions(report.prescriptions);
    }, [report?.prescriptions]);

    // Available filter options
    const availableSeverities = useMemo(() => {
        if (!report?.problems) return [];
        const severities = new Set(report.problems.map(p => p.severity));
        return Array.from(severities).sort((a, b) => {
            const order = { critical: 0, high: 1, medium: 2, low: 3 };
            return order[a] - order[b];
        });
    }, [report?.problems]);

    const availableCategories = useMemo(() => {
        if (!report?.problems) return [];
        const categories = new Set(
            report.problems
                .map(p => p.category)
                .filter((category): category is string => Boolean(category))
        );
        return Array.from(categories).sort();
    }, [report?.problems]);

    const refresh = useCallback(async () => {
        // Clear error state before retry
        setError(null);

        // Clear cache for this data object to ensure fresh data on retry
        if (dataObjectUuid && USE_BACKEND) {
            reportService.clearAnalysisCache();
        }

        return await fetchReport(true);
    }, [fetchReport, dataObjectUuid]);

    return {
        report,
        loading,
        error,
        refreshing,
        filters,
        setFilters,
        filteredProblems,
        groupedPrescriptions,
        refresh,
        lastUpdatedAt,
        availableSeverities: availableSeverities as Severity[],
        availableCategories,
        translationStats,
    };
}